/**
 * Payments data slice: the interface this feature's screens use + the live implementation over the US API
 * (core/http.ts → /api/v1/app/*). The fixture implementation (in-memory Lone Star tenant) is in ./fixture.ts, which is
 * loaded only in dev / VITE_FIXTURES builds. Owned by the payments feature. Shared entity reads (jobs, techs, customers,
 * vehicles, catalog) live in core/entities.ts.
 *
 * Every endpoint is P6's (api/src/modules/pay). Real today: the payout-batch routes (routes/payouts.js) and the tech
 * statement. The others follow P6 §3.14 under `/v1/app/pay/*` and the P6 service return shapes (jobMoney, sendInvoice,
 * publicAccount, listTechCash); list/summary reads without a P6 route yet are marked "assumed".
 * The client never sets COMPLETED / CASH TAKEN / SETTLED: those are side effects of P6 commands (webhook, record-cash,
 * payout-batch approve) decided on the server.
 */
import { APP, type Http } from '@app/core/http';
import { notAvailable, orRollingOut } from '@app/core/live/dto';
import type { Cents, ISO, Page, UUID } from '@app/core/types';

const P = `${APP}/pay`;
const enc = encodeURIComponent;

// ─────────────────────────────────────────────────────────── Stripe Connect (P6 connect.publicAccount)
export interface ConnectStatus {
  status: 'not_connected' | 'onboarding' | 'active' | 'restricted' | 'disconnected';
  charges_enabled: boolean; payouts_enabled: boolean; details_submitted?: boolean;
  currently_due?: string[]; disabled_reason?: string | null;
}

// ─────────────────────────────────────────────────────────── payments (pay.payments)
export type PayMethod = 'card' | 'ach' | 'wallet' | 'cash' | 'check' | 'other' | 'tap';
export type PayStatus = 'pending' | 'succeeded' | 'failed' | 'voided' | 'refunded' | 'partially_refunded';
export interface PaymentRow {
  id: UUID; job_id: UUID | null; job_no: string | null; customer_name: string | null; tech_id: UUID | null;
  method: PayMethod; provider: 'stripe' | 'manual'; amount_cents: Cents; refunded_cents: Cents; fee_cents: Cents;
  status: PayStatus; source: 'stripe_webhook' | 'tech_app' | 'office' | string; card_brand: string | null; card_last4: string | null;
  receipt_no: string | null; void_reason: string | null; created_at: ISO; pay_link: boolean;
}
export interface PaymentQuery { date?: string; method?: string; after?: string; limit?: number }
/** Collected on a tenant-local day (net of refunds), by method and by tech. */
export interface PaymentSummary {
  date: string; collected_cents: Cents; count: number; fee_cents: Cents; refunded_cents: Cents;
  by_method: Record<string, Cents>;
  by_tech: { tech_id: UUID; jobs: number; revenue_cents: Cents; card_cents: Cents; cash_cents: Cents }[];
}

// ─────────────────────────────────────────────────────────── invoices + pay links (pay.documents / pay.pay_links)
export type LinkStatus = 'active' | 'superseded' | 'paid' | 'expired' | 'void';
export interface LinkInfo { id: UUID; status: LinkStatus; expires_at: ISO; last_sent_at: ISO | null; send_count: number; opened_at?: ISO | null; created_at: ISO }
export interface InvoiceView {
  id: UUID; job_id: UUID; number: string; revision: number; status: 'draft' | 'sent' | 'void';
  payment_state: 'unpaid' | 'partially_paid' | 'paid' | 'overpaid' | 'refunded' | 'void';
  total_cents: Cents; paid_cents: Cents; balance_cents: Cents; issued_at: ISO | null; links: LinkInfo[];
}
export interface Settlement {
  job_id: UUID; job_no?: string | null; tech_id: UUID | null; direction: 'business_to_tech' | 'tech_to_business'; amount_cents: Cents;
  tech_payout_cents: Cents; cash_collected_cents: Cents; status: 'pending_cost' | 'unsettled' | 'settled';
  completed_at: ISO; settled_on: ISO | null; payout_id: UUID | null; order_status?: string;
}
/** P6 `jobMoney()`: everything money about one job. */
export interface JobMoney {
  job_id: UUID; order_status: string; assigned_tech_id: UUID | null; invoice: InvoiceView | null;
  payments: PaymentRow[]; settlement: Settlement | null; customer_has_phone: boolean;
}
/** P6 `sendInvoice()` result. The SMS itself is sent by the server after commit. */
export interface SendResult {
  url: string; link_id: UUID; expires_at: ISO; balance_cents: Cents;
  sms: { sent: boolean; dry_run?: boolean; reason?: 'no_phone' | 'opted_out' | 'no_consent' | 'send_failed' };
}
/** Pay-link list row (office "Pay links" tab). */
export interface PayLinkRow extends LinkInfo {
  document_id: UUID; job_id: UUID; job_no: string; customer_name: string; number: string; balance_cents: Cents; total_cents: Cents;
}

// ─────────────────────────────────────────────────────────── offline payments + refunds
/** Offline methods the office can record. `other` = card run on the tech's/shop's own terminal (not through us). */
export type OfflineMethod = 'cash' | 'check' | 'other';
export interface RecordCashBody { amount_cents: Cents; method: OfflineMethod; tech_id: UUID | null; reference?: string; note?: string }
export interface RecordCashResult { payment: PaymentRow; order_status: string; balance_cents: Cents }
export interface RefundBody { payment_id: UUID; amount_cents: Cents; reason: 'manual'; note: string }
export interface RefundRow { id: UUID; payment_id: UUID; amount_cents: Cents; status: 'pending' | 'succeeded' | 'failed' | 'canceled' | 'requires_action'; reason: string; created_at: ISO }

// ─────────────────────────────────────────────────────────── tech ledger + payouts (settlement.js / payouts.js)
export interface TechCashRow {
  tech_id: UUID; tech_name: string | null; cash_held_cents: Cents; oldest_held_at: ISO | null; cash_jobs: number; pending_cost_jobs: number;
  commission_cents: Cents; net_due_cents: Cents;
  status: { mode: 'off' | 'alert' | 'block'; over_threshold: boolean; reason: string | null; blocked: boolean; alert: boolean };
}
export type BatchStatus = 'draft' | 'approved' | 'recorded' | 'void';
export interface Batch {
  id: UUID; period_from: string; period_to: string; status: BatchStatus; total_cents: Cents; created_at: ISO;
  approved_at: ISO | null; approved_by: string | null; recorded_at: ISO | null; payouts?: number;
}
export const RAILS = ['payroll', 'check', 'ach_own', 'cash', 'netted'] as const;
export type Rail = (typeof RAILS)[number];
export interface Payout {
  id: UUID; batch_id: UUID; tech_id: UUID; tech_name: string | null; commission_cents: Cents; cash_held_cents: Cents; net_due_cents: Cents;
  job_ids: UUID[]; rail: Rail; reference: string | null; status: BatchStatus; excluded_pending_cost: number; jobs?: Settlement[];
}
export interface BatchDetail { batch: Batch; payouts: Payout[] }
export interface CreateBatchResult extends BatchDetail { excluded: { tech_id: UUID; excluded_pending_cost: number }[]; awaiting_office_cash_confirmation?: { job_id: UUID; job_no: string | null; tech_id: UUID; order_status: string | null }[]; skipped_in_other_batch: number; timezone: string }

export interface Api {
  connectStatus(): Promise<ConnectStatus>;
  payments(q: PaymentQuery): Promise<Page<PaymentRow>>;
  summary(date: string): Promise<PaymentSummary>;
  links(q: { status?: string }): Promise<PayLinkRow[]>;
  jobMoney(jobId: string): Promise<JobMoney>;
  createInvoice(jobId: string, key: string): Promise<InvoiceView>;
  sendInvoice(documentId: string, key: string): Promise<SendResult>;
  recordCash(jobId: string, body: RecordCashBody, key: string, version: number | undefined): Promise<RecordCashResult>;
  refund(body: RefundBody, key: string): Promise<RefundRow>;
  settlements(): Promise<Settlement[]>;
  techCash(): Promise<TechCashRow[]>;
  batches(): Promise<Batch[]>;
  batch(id: string): Promise<BatchDetail>;
  createBatch(period_from: string, period_to: string, key: string): Promise<CreateBatchResult>;
  approveBatch(id: string, key: string): Promise<{ batch: Batch; settlement_rows: number; jobs_settled: number }>;
  recordBatch(id: string, payouts: { tech_id: UUID; rail: Rail; reference?: string }[], key: string): Promise<{ batch: Batch; remaining: number }>;
  voidBatch(id: string, reason: string, key: string): Promise<{ batch: Batch }>;
  /** Payroll CSV for the batch (a Blob, so the fixture can produce one too). */
  exportCsv(id: string): Promise<{ filename: string; blob: Blob }>;
}

const items = <T>(r: { items: T[] } | T[] | Record<string, T[]>, key: string): T[] =>
  Array.isArray(r) ? r : ((r as Record<string, T[]>)[key] ?? (r as { items: T[] }).items ?? []);

export const live = (http: Http): Api => ({
  // P6 §3.14 GET /v1/pay/connect/status → publicAccount()
  connectStatus: () => http.get<{ account: ConnectStatus } | ConnectStatus>(`${P}/connect/status`).then((r) => ('account' in r ? r.account : r)),
  // assumed (no P6 list route yet → "Rolling out"): GET /v1/app/pay/payments?date=&method=&after=&limit= → {items,next}
  payments: (q) => http.get<Page<PaymentRow>>(`${P}/payments`, { query: { ...q } }).catch(orRollingOut('payments_list')),
  // assumed (no P6 route yet): GET /v1/app/pay/payments/summary?date= (pay.daily_collections_v + by tech)
  summary: (date) => http.get<PaymentSummary>(`${P}/payments/summary`, { query: { date } }).catch(orRollingOut('payments_summary')),
  // assumed (no P6 route yet): GET /v1/app/pay/links?status= → {links}
  links: (q) => http.get<{ links: PayLinkRow[] }>(`${P}/links`, { query: q }).then((r) => items(r, 'links'), orRollingOut('pay_links_list')),
  // real: GET /v1/app/pay/jobs/:jobId → jobMoney()
  jobMoney: (id) => http.get(`${P}/jobs/${enc(id)}`),
  // real: POST /v1/app/pay/invoices {job_id} (lines default to the job's priced lines) → {invoice}
  createInvoice: (job_id, key) => http.post<{ invoice: InvoiceView } | InvoiceView>(`${P}/invoices`, { job_id }, { idempotencyKey: key }).then((r) => ('invoice' in r ? r.invoice : r)),
  // real: POST /v1/app/pay/invoices/:id/send → sendInvoice()
  sendInvoice: (id, key) => http.post(`${P}/invoices/${enc(id)}/send`, {}, { idempotencyKey: key }),
  // real: POST /v1/app/pay/jobs/:jobId/cash {amount_cents, method: cash|check, collected_by, tech_id?, check_no?, note?}
  // (needs jobs.record_offline_payment; the server decides CASH TAKEN). A card run on an outside terminal has no P6 method yet.
  recordCash: (id, body, key, version) => (body.method === 'other' ? notAvailable('offline_card_payment') : http.post<any>(`${P}/jobs/${enc(id)}/cash`, {
    amount_cents: body.amount_cents, method: body.method, collected_by: 'office', ...(body.tech_id ? { tech_id: body.tech_id } : {}),
    ...(body.method === 'check' && body.reference ? { check_no: body.reference.slice(0, 40) } : {}), ...(body.note ? { note: body.note.slice(0, 200) } : {}),
  }, { idempotencyKey: key, version }).then((r) => ({ ...r, payment: r.payment ?? { id: r.payment_id, receipt_no: r.receipt_no ?? null } }))),
  // real: POST /v1/app/pay/refunds {payment_id, amount_cents, reason} (strict: the note stays client-side for now)
  refund: (body, key) => http.post<{ refund: RefundRow } | RefundRow>(`${P}/refunds`, { payment_id: body.payment_id, amount_cents: body.amount_cents, reason: body.reason }, { idempotencyKey: key }).then((r) => ('refund' in r ? r.refund : r)),
  // assumed (no P6 route yet): GET /v1/app/pay/settlements?status=open → {settlements} (pay.job_settlements unsettled + pending_cost)
  settlements: () => http.get<{ settlements: Settlement[] }>(`${P}/settlements`, { query: { status: 'open' } }).then((r) => items(r, 'settlements'), orRollingOut('settlements_list')),
  // P6 §3.14 GET /v1/pay/techs/cash → listTechCash()
  techCash: () => http.get<{ techs: TechCashRow[] }>(`${P}/techs/cash`).then((r) => items(r, 'techs')),
  // real: routes/payouts.js
  batches: () => http.get<{ batches: Batch[] }>(`${P}/payout-batches`).then((r) => items(r, 'batches')),
  batch: (id) => http.get(`${P}/payout-batches/${enc(id)}`),
  createBatch: (period_from, period_to, key) => http.post(`${P}/payout-batches`, { period_from, period_to }, { idempotencyKey: key }),
  approveBatch: (id, key) => http.post(`${P}/payout-batches/${enc(id)}/approve`, {}, { idempotencyKey: key }),
  recordBatch: (id, payouts, key) => http.post(`${P}/payout-batches/${enc(id)}/record`, { payouts }, { idempotencyKey: key }),
  voidBatch: (id, reason, key) => http.post(`${P}/payout-batches/${enc(id)}/void`, reason ? { reason } : {}, { idempotencyKey: key }),
  exportCsv: async (id) => {
    const res = await http.get<Response>(`${P}/payout-batches/${enc(id)}/export.csv`, { raw: true });
    if (!res.ok) throw new Error(`export failed (${res.status})`);
    const cd = res.headers.get('content-disposition') ?? '';
    return { filename: /filename="([^"]+)"/.exec(cd)?.[1] ?? `payout-batch-${id.slice(0, 8)}.csv`, blob: await res.blob() };
  },
});
